/**
 * <GenerateActivityFlow> — « Générer une activité » (template).
 *
 * Le `+` de la liste d'activités, côté parent comme côté élève. Le legacy est
 * `tutor/students/GenerateActivityModal.js` (3 507 l.), une modale de tuteur montée telle quelle
 * dans l'espace parent : ici on ne dessine que ce que le parent atteint. Tombent, avec leur
 * raison : « Activité commune » et le choix multi-élèves (`forStudents`, jamais passé depuis la
 * liste), « Ancienne interface » (masquée sur `isParentMode`), le « Mode programme de révision »
 * (`has_study_program` n'est pas exposé au parent) et le recadrage d'image (web, hors DS).
 *
 * Quatre étapes, et jamais plus de DEUX actions sur une :
 *  - `form`   : le cadrage, et le dépôt de fichier comme autre chemin — une action ;
 *  - `file`   : le fichier lu par Sam — « Créer l'activité » ou « Générer des activités
 *               similaires » ;
 *  - `similar`: « Réviser » ou « S'entraîner », avec « Personnaliser » qui déplie Type et
 *               Instructions (un repli, pas une action) ;
 *  - `results`: les trois énoncés proposés, chacun avec son bouton — une liste OFFRE.
 *   Le legacy posait « Créer l'activité », « Réviser », « S'entraîner » et « Personnaliser » sur
 *   le même écran : quatre réponses à une question qu'on n'avait pas posée. La coupe en deux
 *   étapes ne retire rien et rend la question lisible.
 *
 * Le tuteur (`GenerateActivityModal.js` tel quel, `voice="student"` : il est tutoyé) y ajoute,
 * chaque fois par une prop absente par défaut : le titre « Activité commune » quand il génère pour
 * plusieurs élèves (`formTitle`), la rangée des élèves visés en tête de chaque étape (`lead`), et le
 * « Mode programme de révision » du formulaire (`studyProgram`).
 *
 * Contrôlé : l'étape, le brouillon, les catalogues, le fichier, l'analyse, les envois en cours,
 * les résultats et l'erreur arrivent en props ; les effets (choisir un fichier) sont injectés,
 * comme dans `<AddSchoolAssessmentModal>`.
 *
 * L'élève qui se génère une activité (`selfCreate` dans le legacy) passe par ce même gabarit,
 * `voice="student"`, et n'en parcourt que deux étapes, `form` puis `results` : il ajoute un
 * exercice de la plateforme, pas un fichier à lui (`allowFile={false}`), et ne choisit pas les
 * compétences travaillées (`formFields` sans `skillParts`, la route lui est fermée). Ce que son
 * cadrage a de plus que celui du parent est décrit dans `<ActivityGenerationForm>`.
 */
import React from "react"
import { type ScrollView, type View } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import {
    type UIActivityAnalysis,
    type UIActivityFile,
    type UIActivityGenerationCatalogs,
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
    type UIActivityGenerationMissingField,
    type UIActivityGenerationStep,
    type UIGeneratedActivity,
    type UISchoolAssessment,
} from "../../../ports/primary/ui/types"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { WaitingText } from "../../atoms/WaitingText"
import { Modal } from "../../molecules/Modal"
import { ActivityAnalysisNotice } from "../../molecules/ActivityAnalysisNotice"
import { ActivityFileDropZone } from "../../organisms/ActivityFileDropZone"
import { ActivityGenerationForm, type ActivityGenerationStudyProgram } from "../../organisms/ActivityGenerationForm"
import { ActivityMediaList } from "../../organisms/ActivityMediaList"
import { GeneratedActivityResults } from "../../organisms/GeneratedActivityResults"
import { type Voice } from "../../utils/voice"

/** La ligne de statut pendant que Sam travaille, par lecteur (`TEXTS` de `GenerateActivityModal.js`). */
const TEXTS: Record<Voice, { composing: string }> = {
    parent: { composing: "Sam compose l'activité…" },
    student: { composing: "Sam compose ton activité…" },
}

/** Le titre de la feuille, par étape — les mots du legacy. */
const STEP_TITLES: Record<UIActivityGenerationStep, string> = {
    form: "Générer une activité",
    file: "Depuis un fichier...",
    similar: "Activités similaires",
    results: "Énoncés générés",
}

export type GenerateActivityFlowProps = {
    visible: boolean
    onClose: () => void
    step: UIActivityGenerationStep

    draft: UIActivityGenerationDraft
    onDraftChange: (next: UIActivityGenerationDraft) => void
    catalogs: UIActivityGenerationCatalogs
    chaptersLoading?: boolean
    /** Les chapitres n'ont pas pu se charger : le champ le dit et propose de réessayer. */
    chaptersFailed?: boolean
    onRetryChapters?: () => void
    /** Les compétences du chapitre choisi arrivent. */
    skillPartsLoading?: boolean
    /** Le chapitre est exigé. Faux quand le couple classe + matière n'a aucun chapitre validé. */
    chapterRequired?: boolean
    /** Les centres d'intérêt de l'enfant — la chip « Personnaliser ». */
    studentInterests?: string | null

    /** « 4ème · Mathématiques », pour la zone de dépôt et l'encart d'analyse. */
    scopeLabel?: string | null
    /** Ouvre le sélecteur de fichier — effet injecté. */
    onPickFile?: () => void
    /** Un fichier est survolé (web). */
    dragActive?: boolean
    /** Le fichier déposé, montré à l'étape `file`. */
    file?: UIActivityFile | null
    /** Retire le fichier et revient au formulaire. */
    onRemoveFile?: () => void

    analysis?: UIActivityAnalysis
    onAcceptCrossClass?: () => void
    onDismissCrossClass?: () => void

    /**
     * Ce qui manque au formulaire pour générer, `null` quand rien ne manque — l'écran décide
     * (chapitre choisi, ou instructions assez longues). Le bouton reste touchable : un toucher
     * incomplet amène à ce champ et dit quoi choisir.
     */
    missingField?: UIActivityGenerationMissingField | null
    /** Prêt à partir depuis le fichier lu (étape `file`). */
    canSubmit?: boolean
    /** Génère trois énoncés à partir du formulaire. */
    onGenerate?: () => void
    /** Crée l'activité directement depuis le fichier lu. */
    onCreateFromFile?: () => void
    /** Ouvre l'étape des activités similaires. */
    onOpenSimilar?: () => void
    /** Génère un parcours similaire — la valeur est celle d'une option de `catalogs.levels`. */
    onGenerateSimilar?: (level: string) => void
    /** Une génération ou une création est en cours. */
    generating?: boolean
    /** L'activité a été créée depuis le fichier : le bouton le dit et ne repart pas. */
    created?: boolean

    /** Les trois énoncés proposés. */
    results?: UIGeneratedActivity[]
    /** L'option « Quiz de prérequis » est offerte sur chaque carte. */
    quizAvailable?: boolean
    onToggleResultQuiz?: (id: string | number, next: boolean) => void
    onCreateResult?: (id: string | number) => void
    /** Refait générer trois énoncés neufs au lieu de ceux déjà proposés. Absent : pas de bouton. */
    onRegenerate?: () => void

    onBack?: () => void
    error?: string | null

    /** À qui la feuille parle. Défaut `"parent"`. */
    voice?: Voice
    /** Le dépôt d'un fichier à faire lire par Sam, à l'étape `form`. Défaut `true`. */
    allowFile?: boolean
    /** Les champs du cadrage, à l'étape `form`. Défaut : tous (`<ActivityGenerationForm fields>`). */
    formFields?: UIActivityGenerationField[]
    /** Le prochain contrôle de la matière choisie : ses chapitres remontent en tête de liste. */
    nextAssessment?: UISchoolAssessment | null
    /** Un chapitre OU des instructions font avancer (`<ActivityGenerationForm chapterOrInstructions>`). */
    chapterOrInstructions?: boolean
    /** La recherche de chapitre sans résultat bascule vers Instructions, avec le texte cherché. */
    onDescribeInInstructions?: (searchText: string) => void
    /** Instructions est le chemin pris après cette bascule : « Sam créera le chapitre ». */
    instructionsRecommended?: boolean
    /** Le titre de l'étape `form`. Défaut : « Générer une activité » (le tuteur : « Activité commune »). */
    formTitle?: string
    /** En tête de chaque étape — les élèves pour qui le tuteur génère. Absent : rien. */
    lead?: React.ReactNode
    /** Le « Mode programme de révision » du formulaire (`<ActivityGenerationForm studyProgram>`). */
    studyProgram?: ActivityGenerationStudyProgram
    /**
     * « Générer » est verrouillé à l'étape `form`, et dit pourquoi : son libellé et un cadenas (le chapitre
     * d'un exo en cours d'enregistrement). Absent (défaut) : le bouton d'avant.
     */
    submitLock?: { label: string } | null
    /** Le libellé de « Générer » à l'étape `form` quand il dit autre chose (« Générer sans chapitre »). */
    submitLabel?: string
    /**
     * Un avertissement en tête de l'étape, à lire avant de générer — pas une erreur (« Chapitre non fiable » :
     * choisis le chapitre de l'exo, ou il n'y en a aucun). Absent : rien.
     */
    notice?: string | null
    testID?: string
}

export function GenerateActivityFlow(props: GenerateActivityFlowProps) {
    const {
        visible,
        onClose,
        step,
        draft,
        onDraftChange,
        catalogs,
        chaptersLoading = false,
        chaptersFailed = false,
        onRetryChapters,
        skillPartsLoading = false,
        chapterRequired = true,
        studentInterests,
        scopeLabel,
        onPickFile,
        dragActive = false,
        file = null,
        onRemoveFile,
        analysis,
        onAcceptCrossClass,
        onDismissCrossClass,
        missingField = null,
        canSubmit = false,
        onGenerate,
        onCreateFromFile,
        onOpenSimilar,
        onGenerateSimilar,
        generating = false,
        created = false,
        results = [],
        quizAvailable = false,
        onToggleResultQuiz,
        onCreateResult,
        onRegenerate,
        onBack,
        error = null,
        voice = "parent",
        allowFile = true,
        formFields,
        nextAssessment,
        chapterOrInstructions = false,
        onDescribeInInstructions,
        instructionsRecommended = false,
        formTitle,
        lead,
        studyProgram,
        submitLock = null,
        submitLabel,
        notice = null,
        testID,
    } = props

    /** « Personnaliser » déplie Type et Instructions : un repli, donc un état d'interface. */
    const [customOpen, setCustomOpen] = React.useState(false)

    // Un toucher sur « Générer » alors qu'il manque une information : le champ passe en défaut et
    // vient à l'écran. Le défaut suit ce qui manque — il passe au champ suivant, ou s'éteint.
    const { theme } = useUnistyles()
    const scrollRef = React.useRef<ScrollView>(null)
    const fieldNodes = React.useRef<Partial<Record<UIActivityGenerationMissingField, View | null>>>({})
    const [fixRequest, setFixRequest] = React.useState(0)
    const fieldToFix = fixRequest > 0 ? missingField : null
    React.useEffect(() => {
        if (missingField == null || step !== "form") setFixRequest(0)
    }, [missingField, step])
    React.useEffect(() => {
        if (fixRequest === 0 || missingField == null) return
        const node = fieldNodes.current[missingField]
        const scroll = scrollRef.current
        if (node == null || scroll == null) return
        // La vue du contenu, pas son numéro (`getInnerViewNode`) : la nouvelle architecture refuse
        // de mesurer par rapport à un numéro, et le défilement ne partirait jamais sur un téléphone.
        // `getInnerViewRef` existe en natif comme sur le web, mais les types de React Native l'ignorent.
        const content = (scroll as ScrollView & { getInnerViewRef: () => View | null }).getInnerViewRef()
        if (content == null) return
        node.measureLayout(
            content,
            // Un peu d'air au-dessus du champ : son libellé ne colle pas à l'en-tête.
            (_x, y) => scroll.scrollTo({ y: Math.max(0, y - theme.spacing["2"]), animated: true }),
            () => undefined
        )
        // Un nouveau toucher ramène au champ, même si ce qui manque n'a pas changé.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixRequest])
    const fieldRef = React.useCallback((field: UIActivityGenerationMissingField, node: View | null) => {
        fieldNodes.current[field] = node
    }, [])
    const submitForm = () => {
        if (missingField != null) {
            setFixRequest((request) => request + 1)
            return
        }
        onGenerate?.()
    }

    const headerLeft =
        step !== "form" && onBack != null ? (
            <IconButton
                iconName="chevron-left"
                appearance="transparent"
                iconColor="primary"
                onPress={onBack}
                accessibilityLabel="Revenir à l'étape précédente"
            />
        ) : undefined

    const composingLabel = TEXTS[voice].composing

    const composing = (
        <WaitingText size="text-sm" weight="regular" color="text-muted" numberOfLines={2}>
            {composingLabel}
        </WaitingText>
    )

    const footer = renderFooter()

    function renderFooter(): React.ReactNode {
        if (step === "form") {
            return (
                <Stack gap="2">
                    {generating && composing}
                    <Button
                        label={submitLock?.label ?? submitLabel ?? "Générer une activité"}
                        iconName={submitLock != null ? "lock" : "magic"}
                        fullWidth
                        disabled={generating || submitLock != null}
                        loading={generating}
                        onPress={submitForm}
                    />
                </Stack>
            )
        }

        if (step === "file") {
            return (
                <Stack gap="2">
                    {generating && composing}
                    <Row gap="2" justify="center">
                        <Button
                            label={created ? "Activité créée" : "Créer l'activité"}
                            iconName={created ? "check" : "check-circle"}
                            color="success"
                            disabled={!canSubmit || generating || created}
                            loading={generating}
                            onPress={onCreateFromFile}
                        />
                        <Button
                            label="Activités similaires"
                            variant="outline"
                            iconName="magic"
                            disabled={!canSubmit || generating}
                            onPress={onOpenSimilar}
                        />
                    </Row>
                </Stack>
            )
        }

        if (step === "similar") {
            return (
                <Stack gap="2">
                    {generating && composing}
                    <Row gap="2" justify="center">
                        <Button
                            label={customOpen ? "Générer" : "Réviser"}
                            iconName={customOpen ? "magic" : "redo"}
                            disabled={generating}
                            loading={generating}
                            onPress={() => onGenerateSimilar?.(customOpen ? draft.level : "1")}
                        />
                        {!customOpen && (
                            <Button
                                label="S'entraîner"
                                variant="outline"
                                iconName="dumbbell"
                                disabled={generating}
                                onPress={() => onGenerateSimilar?.("2")}
                            />
                        )}
                    </Row>
                    <Row justify="center">
                        <TextButton
                            label={customOpen ? "Masquer la personnalisation" : "Personnaliser"}
                            onPress={() => setCustomOpen((open) => !open)}
                        />
                    </Row>
                </Stack>
            )
        }

        return (
            <Stack gap="2" align="center">
                {onRegenerate != null && (
                    <Button
                        label="Autres énoncés"
                        variant="outline"
                        iconName="redo"
                        disabled={results.some((result) => result.status === "pending")}
                        onPress={onRegenerate}
                    />
                )}
                <Button label="Terminer" variant="outline" onPress={onClose} />
            </Stack>
        )
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={step === "form" && formTitle != null ? formTitle : STEP_TITLES[step]}
            fullScreenOnSmall
            // L'étape des similaires tient en une phrase : une feuille pleine hauteur pour deux
            // boutons laisserait 600 px de blanc au milieu.
            sizeToContent={step === "similar"}
            dismissable
            headerLeft={headerLeft}
            footer={footer}
            scrollRef={scrollRef}
            testID={testID}
        >
            <Stack gap="5">
                {lead}
                {error != null && error !== "" && (
                    <Banner color="danger" tinted icon="exclamation-circle" message={error} />
                )}
                {notice != null && notice !== "" && (
                    <Banner color="warning" tinted icon="exclamation-triangle" message={notice} />
                )}

                {step === "form" && (
                    <>
                        <ActivityGenerationForm
                            value={draft}
                            onChange={onDraftChange}
                            catalogs={catalogs}
                            chaptersLoading={chaptersLoading}
                            chaptersFailed={chaptersFailed}
                            onRetryChapters={onRetryChapters}
                            skillPartsLoading={skillPartsLoading}
                            chapterRequired={chapterRequired}
                            studentInterests={studentInterests}
                            fields={formFields}
                            fieldToFix={fieldToFix}
                            fieldRef={fieldRef}
                            voice={voice}
                            nextAssessment={nextAssessment}
                            chapterOrInstructions={chapterOrInstructions}
                            onDescribeInInstructions={onDescribeInInstructions}
                            instructionsRecommended={instructionsRecommended}
                            studyProgram={studyProgram}
                            disabled={generating}
                        />
                        {allowFile && (
                            <ActivityFileDropZone
                                onPickFile={onPickFile}
                                dragActive={dragActive}
                                disabled={draft.className == null || draft.subjectId == null}
                                contextLabel={scopeLabel}
                                voice={voice}
                            />
                        )}
                    </>
                )}

                {step === "file" && file != null && (
                    <>
                        <Stack gap="2">
                            <Row justify="between" align="center">
                                <SectionLabel>Le fichier</SectionLabel>
                                {onRemoveFile != null && <TextButton label="Retirer" onPress={onRemoveFile} />}
                            </Row>
                            <ActivityMediaList
                                media={[
                                    file.mimeType === "application/pdf"
                                        ? { kind: "pdf", uri: file.uri, name: file.name }
                                        : { kind: "image", uri: file.uri },
                                ]}
                                thumbnailSize={200}
                                fit="contain"
                            />
                        </Stack>
                        <ActivityGenerationForm
                            value={draft}
                            onChange={onDraftChange}
                            catalogs={catalogs}
                            chaptersLoading={chaptersLoading}
                            skillPartsLoading={skillPartsLoading}
                            chapterRequired={chapterRequired}
                            // Un fichier lu répond déjà au type et aux instructions : seul le
                            // chapitre reste à confirmer, plus la durée de l'activité à créer.
                            fields={["chapter", "skillParts", "duration"]}
                            chaptersFailed={chaptersFailed}
                            onRetryChapters={onRetryChapters}
                            voice={voice}
                            disabled={generating}
                            analysisSlot={
                                analysis != null ? (
                                    <ActivityAnalysisNotice
                                        analysis={analysis}
                                        scopeLabel={scopeLabel ?? undefined}
                                        hasChapter={draft.chapterId != null}
                                        onAcceptCrossClass={onAcceptCrossClass}
                                        onDismissCrossClass={onDismissCrossClass}
                                        voice={voice}
                                    />
                                ) : undefined
                            }
                        />
                    </>
                )}

                {step === "similar" && (
                    <Stack gap="5">
                        <Text color="text-muted">Sam compose trois exercices du même chapitre.</Text>
                        {customOpen && (
                            <ActivityGenerationForm
                                value={draft}
                                onChange={onDraftChange}
                                catalogs={catalogs}
                                // Le repli « Personnaliser » du legacy : le type, les instructions,
                                // et rien d'autre — le fichier a déjà donné le cadrage.
                                fields={["level", "instructions"]}
                                chapterRequired={false}
                                studentInterests={studentInterests}
                                voice={voice}
                                disabled={generating}
                            />
                        )}
                    </Stack>
                )}

                {step === "results" && (
                    <GeneratedActivityResults
                        activities={results}
                        quizAvailable={quizAvailable}
                        onToggleQuiz={onToggleResultQuiz}
                        onCreate={onCreateResult}
                        voice={voice}
                    />
                )}
            </Stack>
        </Modal>
    )
}
